import { ApolloClient, ErrorLike } from '@apollo/client'
import Cookies from 'universal-cookie'

import {
  CartFragment,
  CartLineInput,
  CartUserError,
} from '../../__generated__/graphql/catalog/graphql'
import { reportClientError } from '../../app/_components/chrome/scripts/DataDogRumScript'
import { convertCookiesToAccountAuthorization } from '../../app/_config/Authentication.config'
import { cookieKeys } from '../../app/_config/Cookies.config'
import { sessionStorageKeys } from '../../app/_config/Session.config'
import { CART_ATTRIBUTES_UPDATE } from '../graphql/mutations/CART_ATTRIBUTES_UPDATE'
import { CART_CREATE } from '../graphql/mutations/CART_CREATE'
import { CART_LINES_ADD } from '../graphql/mutations/CART_LINES_ADD'
import { CART_LINES_UPDATE } from '../graphql/mutations/CART_LINES_UPDATE'
import { CHECKOUT_CART_CREATE } from '../graphql/mutations/CHECKOUT_CART_CREATE'
import { CREATE_LOOP_RETURN_EXCHANGE_CART } from '../graphql/mutations/CREATE_LOOP_RETURN_EXCHANGE_CART'
import { SYNC_RETURN_EXCHANGE_CART } from '../graphql/mutations/SYNC_RETURN_EXCHANGE_CART'
import { FETCH_CART, FETCH_CART_EXISTS } from '../graphql/queries/FETCH_CART'
import { FETCH_CHECKOUT_URL } from '../graphql/queries/FETCH_CHECKOUT_URL'

import { CheckoutLineItemInput, CustomAttribute, ICheckoutClient } from './interfaces'

enum IterableKeys {
  CAMPAIGN_ID = 'iterableEmailCampaignId',
  SMS_CAMPAIGN_ID = 'iterableEmailCampaignIdTemp',
  TEMPLATE_ID = 'iterableTemplateId',
  MESSAGE_ID = 'iterableMessageId',
  USER_ID = 'iterableUserId',
}

type ErrorContext = {
  scope: string
  error: ErrorLike | undefined
  userErrors: CartUserError[] | string | null | undefined
}

export class CartServiceApiClient implements ICheckoutClient {
  private cookies = new Cookies()
  private account = convertCookiesToAccountAuthorization({
    authCookie: this.cookies.get(cookieKeys.authToken.key) ?? undefined,
    authRefreshCookie: this.cookies.get(cookieKeys.authRefreshToken.key) ?? undefined,
  })
  private apolloClient: ApolloClient

  constructor({ apolloClient }: { apolloClient: ApolloClient }) {
    this.apolloClient = apolloClient
  }

  /**
   * Helper that builds Iterable attribution values.
   *
   * Reads cookies to determine if a user originated from an Iterable SMS or Email campaign.
   */
  private get _iterableAttributionValues(): { key: string; value: string }[] {
    const iterableKeys = [
      IterableKeys.CAMPAIGN_ID,
      IterableKeys.MESSAGE_ID,
      IterableKeys.TEMPLATE_ID,
      IterableKeys.USER_ID,
      IterableKeys.SMS_CAMPAIGN_ID,
    ]
    const values: { key: string; value: string }[] = []

    iterableKeys.forEach(key => {
      const value = this.cookies.get(key) ?? ''

      // currently we map both email and sms campaigns to the same checkout attribute but sms takes precedence
      if (key === IterableKeys.SMS_CAMPAIGN_ID) {
        key = IterableKeys.CAMPAIGN_ID
      }

      values.push({ key, value })
    })

    return values
  }

  /**
   * Helper that builds custom attributes.
   *
   * Reads cookies and session storage to determine if a customer is logged in,
   * obtains the current location, and appends any iterable attribution values.
   */
  private get _customAttributes(): CustomAttribute[] {
    const isLoggedIn = !!this.account
    const iterableValues = this._iterableAttributionValues
    const currentLocation =
      globalThis.document.location.protocol +
      '//' +
      globalThis.document.location.host +
      globalThis.document.location.pathname +
      globalThis.document.location.search
    const landingLocation = globalThis.sessionStorage.getItem(sessionStorageKeys.landingPage)
    const staticCustomAttributes: CustomAttribute[] = [{ key: 'appSource', value: 'web' }]

    const customAttributes: CustomAttribute[] = [
      ...staticCustomAttributes,
      {
        key: 'landing_site',
        value: landingLocation ?? currentLocation,
      },
      {
        key: 'is_customer_logged_in',
        value: String(isLoggedIn),
      },
    ]
    iterableValues.forEach(item => {
      if (item.value) {
        customAttributes.push(item)
      }
    })
    return customAttributes
  }

  /**
   * Helper that reports userErrors and GraphQL Errors to DataDog.
   *
   * @param sourceFunction the function providing the errors to process
   * @param errorContext the context for the errors being processed
   */
  private reportErrors(sourceFunction: string, errorContext: ErrorContext): void {
    if (Array.isArray(errorContext.userErrors)) {
      errorContext.userErrors.forEach(err => {
        if (err.code === 'INVALID') {
          reportClientError({
            error: new Error(`${sourceFunction} :: invalid merchandise in cart`),
            context: {
              ...errorContext,
              label: 'error code `INVALID` received from app-figs-cart-service',
            },
          })
        } else if (err.code === 'VALIDATION_CUSTOM') {
          reportClientError({
            error: new Error(`${sourceFunction} :: custom checkout validation error`),
            context: {
              ...errorContext,
              label: 'error code `VALIDATION_CUSTOM` received from app-figs-cart-service',
            },
          })
        } else if (err.code === 'INVALID_MERCHANDISE_LINE') {
          reportClientError({
            error: new Error(`${sourceFunction} :: invalid merchandiseId passed`),
            context: {
              ...errorContext,
              label: 'invalid merchandiseId passed on cart line',
            },
          })
        } else if (err.message.includes("'gid://shopify/ProductVariant/'")) {
          reportClientError({
            error: new Error(`${sourceFunction} :: empty merchandiseId passed`),
            context: {
              ...errorContext,
              label: 'empty merchandiseId passed on cart line',
            },
          })
        } else {
          reportClientError({
            error: new Error(`${sourceFunction} :: unknown userError occurred`),
            context: {
              ...errorContext,
              label: 'unknown userError received from app-figs-cart-service',
            },
          })
        }
      })
    } else if (typeof errorContext.userErrors === 'string') {
      reportClientError({
        error: new Error(`${sourceFunction} :: unknown userError occurred`),
        context: {
          ...errorContext,
          label: 'unknown userError received from app-figs-cart-service',
        },
      })
    }
    if (errorContext.error) {
      reportClientError({
        error: new Error(`${sourceFunction} :: GraphQL errors occurred`),
        context: {
          ...errorContext,
          label: `${sourceFunction} errors`,
        },
      })
    }
  }

  /**
   * Helper that performs the `CHECKOUT_CART_CREATE` mutation.
   *
   * @param items items to add to the cart/checkout
   * @param discountCodes discount codes to apply to the cart/checkout
   * @param globalCustomAttributes attribute metadata to add to the cart/checkout
   * @throws an error if no data is returned.
   * @returns resulting cart or errors
   */
  private async _createCheckoutFromCartService(
    items: CheckoutLineItemInput[],
    discountCodes?: string[],
    globalCustomAttributes?: CustomAttribute[]
  ) {
    const cartLineItems: CartLineInput[] = items.map(item => ({
      merchandiseId: item.variantId.replace('gid://shopify/ProductVariant/', ''),
      quantity: item.quantity,
      attributes: (item.customAttributes ?? []).map(attr => ({
        key: attr.key,
        value: String(attr.value),
      })),
    }))

    const result = await this.apolloClient.mutate({
      mutation: CHECKOUT_CART_CREATE,
      variables: {
        input: {
          lines: cartLineItems,
          discountCodes: discountCodes,
          attributes: globalCustomAttributes,
        },
      },
      context: {
        retryOnFailure: true,
      },
      errorPolicy: 'all',
    })

    return result
  }

  /**
   * Creates a checkout via the cart service.
   *
   * It delegates to `_createCheckoutFromCartService`, performs error checking,
   * and appends additional query parameters (such as discount codes and `FBCLID`).
   *
   * @param items items to add to the cart/checkout
   * @param discountCodes discount codes to apply to the cart/checkout
   * @param globalCustomAttributes attribute metadata to add to the cart/checkout
   * @throws an error if no data is returned, or if checkout creation fails
   * @returns `checkoutId` and `checkoutUrl` as `string`
   */
  public async createCheckoutWithCartItems({
    items,
    discountCodes,
    globalCustomAttributes,
  }: Parameters<ICheckoutClient['createCheckoutWithCartItems']>[0]): ReturnType<
    ICheckoutClient['createCheckoutWithCartItems']
  > {
    const customAttributes = [...this._customAttributes, ...(globalCustomAttributes || [])]
    let { data, error } = await this._createCheckoutFromCartService(
      items,
      discountCodes,
      customAttributes
    )

    // Define error context properties that should be captured across all reported checkout errors
    const sharedErrorContext: ErrorContext = {
      scope: 'checkout',
      error,
      userErrors: data?.cartCreate.userErrors,
    }

    // report any userErrors or GraphQL errors that are present
    this.reportErrors('createCheckoutWithCartItems', sharedErrorContext)

    if (!data) {
      reportClientError({
        error: new Error(
          'checkoutCartCreate :: no data returned from CHECKOUT_CART_CREATE mutation'
        ),
        context: { ...sharedErrorContext, label: 'checkoutCartCreate missing data' },
      })
      throw new Error('Checkout creation failed: missing data.')
    }

    const shopifyCart = data.cartCreate.cart
    if (!shopifyCart?.id) {
      reportClientError({
        error: new Error('checkoutCartCreate :: no cart ID in response'),
        context: { ...sharedErrorContext, label: 'checkoutCartCreate missing cart ID' },
      })
      throw new Error('Checkout creation failed: missing cart ID.')
    }

    const checkoutString = shopifyCart.checkoutUrl
    if (!checkoutString) {
      reportClientError({
        error: new Error('checkoutCartCreate :: no checkoutUrl in response'),
        context: { ...sharedErrorContext, label: 'checkoutCartCreate missing checkoutUrl' },
      })
      throw new Error('Checkout creation failed: missing checkoutUrl.')
    }

    const cartId = shopifyCart.id
    const checkoutUrl = new URL(checkoutString)

    const discountCode = globalThis.sessionStorage.getItem(sessionStorageKeys.discount)
    if (discountCode) {
      checkoutUrl.searchParams.append('discount', discountCode)
    }

    if (discountCodes && discountCodes.length > 0) {
      checkoutUrl.searchParams.append('discount', discountCodes[0]!)
    }

    const facebookCookieVal = this.cookies.get(cookieKeys.facebook.key)
    if (facebookCookieVal) {
      checkoutUrl.searchParams.append('FBCLID', facebookCookieVal)
    }

    return { checkoutUrl, checkoutId: cartId }
  }

  /**
   * Checks if a given cart has completed checkout.
   *
   * If lookup succeeds, we will get a populated `Cart` back indicating the cart is still active.
   * Otherwise, we will receive an `null` response indicating the cart/checkout is completed.
   *
   * @param cartId the id of the cart to check the status of
   * @returns whether or not the given cart has completed checkout
   */
  public async isCheckoutCompleted(
    cartId: Parameters<ICheckoutClient['isCheckoutCompleted']>[0]
  ): ReturnType<ICheckoutClient['isCheckoutCompleted']> {
    const { data, error } = await this.apolloClient.query({
      query: FETCH_CART_EXISTS,
      variables: {
        cartId: cartId,
      },
      fetchPolicy: 'network-only',
    })
    if (error) throw error
    return data?.cart.cart === null
  }

  /**
   * Retrieves a cart by id.
   *
   * @param cartId the id of the cart to retrieve
   * @throws error from `ApolloClient`, if present
   * @returns the found cart
   */
  public async fetchCart(
    cartId: Parameters<ICheckoutClient['fetchCart']>[0]
  ): Promise<CartFragment | null> {
    const { data, error } = await this.apolloClient.query({
      query: FETCH_CART,
      variables: {
        cartId: cartId,
      },
      fetchPolicy: 'network-only',
    })
    if (error) throw error
    return data?.cart.cart ?? null
  }

  /**
   * Create a cart, optionally accepting props for attributes, line items, etc.
   *
   * @param props.attributes cart attributes to set on the created cart
   * @param props.discountCodes discount codes to apply to the created cart
   * @param props.giftCardCodes gift cards to apply to the created cart
   * @param props.lines line items to add to the created cart
   * @param props.metafields metafields to apply to set on the created cart
   * @param props.note a note to pass/apply to the created cart/checkout
   * @throws an error if no data is returned, or if cart creation fails
   * @returns the created cart if successful, `null` otherwise
   */
  public async createCart(
    props: Parameters<ICheckoutClient['createCart']>[0]
  ): ReturnType<ICheckoutClient['createCart']> {
    // standardize lines, specifically `merchandiseId` format
    const standardizedLines = props.lines.map(line => ({
      ...line,
      merchandiseId: line.merchandiseId.replace('gid://shopify/ProductVariant/', ''),
    }))

    // attempt to create cart
    let { data, error } = await this.apolloClient.mutate({
      mutation: CART_CREATE,
      variables: {
        input: {
          ...props,
          lines: standardizedLines,
          // pass starting attributes on cart creation
          attributes: this._customAttributes,
        },
      },
      context: {
        retryOnFailure: true,
      },
      errorPolicy: 'all',
    })

    const sharedErrorContext: ErrorContext = {
      scope: 'cart',
      error,
      userErrors: data?.cartCreate.userErrors,
    }

    // report any userErrors or GraphQL errors that are present
    this.reportErrors('createCart', sharedErrorContext)

    if (!data) {
      reportClientError({
        error: new Error('cartCreate :: no data returned from CART_CREATE mutation'),
        context: {
          ...sharedErrorContext,
          label: 'cartCreate missing data',
        },
      })
      throw new Error('Cart creation failed: missing data.')
    }

    if (!data.cartCreate.cart?.id) {
      reportClientError({
        error: new Error('cartCreate :: no cart ID in response'),
        context: {
          label: 'cartCreate missing cart ID',
          ...sharedErrorContext,
        },
      })
      throw new Error('Cart creation failed: missing cart ID.')
    }

    return data.cartCreate.cart
  }

  /**
   * Create a Loop Return/Exchange cart, which will be used for Loop Shop Now flows.
   *
   * @param cartId - the Ecomm/Shopify cart id
   * @returns a Loop cart token
   */
  public async createLoopReturnExchangeCart(
    cartId: Parameters<ICheckoutClient['createLoopReturnExchangeCart']>[0]
  ): ReturnType<ICheckoutClient['createLoopReturnExchangeCart']> {
    const { data, error } = await this.apolloClient.mutate({
      mutation: CREATE_LOOP_RETURN_EXCHANGE_CART,
      variables: {
        cartId,
      },
      context: {
        retryOnFailure: true,
      },
      errorPolicy: 'all',
    })

    const sharedErrorContext: ErrorContext = {
      scope: 'cart',
      error,
      userErrors: data?.createLoopCart.error,
    }

    // report any userErrors or GraphQL errors that are present
    this.reportErrors('createLoopReturnExchangeCart', sharedErrorContext)

    if (!data) {
      reportClientError({
        error: new Error(
          'createLoopReturnExchangeCart :: no data returned from CREATE_LOOP_RETURN_EXCHANGE_CART mutation'
        ),
        context: {
          ...sharedErrorContext,
          label: 'createLoopReturnExchangeCart missing data',
        },
      })
      throw new Error('Loop return/exchange cart creation failed: missing data.')
    }

    if (!data.createLoopCart.token) {
      reportClientError({
        error: new Error('createLoopReturnExchangeCart :: no cart token in response'),
        context: {
          label: 'createLoopReturnExchangeCart missing cart token',
          ...sharedErrorContext,
        },
      })
      throw new Error('Loop return/exchange cart creation failed: missing cart token.')
    }

    return data.createLoopCart.token
  }

  /**
   * Sync the Ecomm/Shopify cart with a Loop cart
   *
   * @param cartToken  - the Loop cart token
   * @returns an Ecomm/Shopify cart, synced to the Loop cart contents
   */
  public async syncReturnExchangeCart(
    cartToken: Parameters<ICheckoutClient['syncReturnExchangeCart']>[0]
  ): ReturnType<ICheckoutClient['syncReturnExchangeCart']> {
    const { data, error } = await this.apolloClient.mutate({
      mutation: SYNC_RETURN_EXCHANGE_CART,
      variables: {
        cartToken,
      },
      context: {
        retryOnFailure: true,
      },
      errorPolicy: 'all',
    })

    const sharedErrorContext: ErrorContext = {
      scope: 'cart',
      error,
      userErrors: data?.syncReturnExchangeCart.userErrors,
    }

    // report any userErrors or GraphQL errors that are present
    this.reportErrors('syncReturnExchangeCart', sharedErrorContext)

    if (!data) {
      reportClientError({
        error: new Error(
          'syncReturnExchangeCart :: no data returned from SYNC_RETURN_EXCHANGE_CART mutation'
        ),
        context: {
          ...sharedErrorContext,
          label: 'syncReturnExchangeCart missing data',
        },
      })
      throw new Error('Loop cart sync failed: missing data.')
    }

    if (!data.syncReturnExchangeCart.cart?.id) {
      reportClientError({
        error: new Error('syncReturnExchangeCart :: no cart ID in response'),
        context: {
          label: 'syncReturnExchangeCart missing cart ID',
          ...sharedErrorContext,
        },
      })
      throw new Error('Loop cart sync failed: missing cart ID.')
    }

    return data.syncReturnExchangeCart.cart
  }

  /**
   * Add line items to the provided cart
   * @param props.cartId the id of the cart to add lines to
   * @param props.lines the lines to add to the cart
   * @returns the updated cart, plus any userErrors and warnings (stock problems arrive as warnings)
   */
  public async addLines({
    cartId,
    lines,
  }: Parameters<ICheckoutClient['addLines']>[0]): ReturnType<ICheckoutClient['addLines']> {
    // standardize lines, specifically `merchandiseId` format
    const standardizedLines = lines.map(line => ({
      ...line,
      merchandiseId: line.merchandiseId.replace('gid://shopify/ProductVariant/', ''),
    }))

    let { data, error } = await this.apolloClient.mutate({
      mutation: CART_LINES_ADD,
      variables: {
        cartId,
        lines: standardizedLines,
      },
      context: {
        retryOnFailure: true,
      },
      errorPolicy: 'all',
    })

    const sharedErrorContext: ErrorContext = {
      scope: 'cart',
      error,
      userErrors: data?.cartLinesAdd.userErrors,
    }

    // report any userErrors or GraphQL errors that are present
    this.reportErrors('addLines', sharedErrorContext)

    if (!data) {
      reportClientError({
        error: new Error('addLines :: no data returned from CART_LINES_ADD mutation'),
        context: {
          ...sharedErrorContext,
          label: 'addLines missing data',
        },
      })
      throw new Error('cart lines add failed: missing data.')
    }

    if (!data.cartLinesAdd.cart?.id) {
      reportClientError({
        error: new Error('addLines :: No cart ID in response'),
        context: {
          ...sharedErrorContext,
          label: 'addLines missing cart ID',
        },
      })
      throw new Error('cart lines add failed: missing cart ID.')
    }

    return {
      cart: data.cartLinesAdd.cart,
      userErrors: data.cartLinesAdd.userErrors ?? [],
      warnings: data.cartLinesAdd.warnings ?? [],
    }
  }

  /**
   * Update line items in the provided cart
   * @param props.cartId the id of the cart to update lines on
   * @param props.lines the lines to update in the cart
   * @returns the updated cart if successful, `null` otherwise
   */
  public async updateLines({
    cartId,
    lines,
  }: Parameters<ICheckoutClient['updateLines']>[0]): ReturnType<ICheckoutClient['updateLines']> {
    let { data, error } = await this.apolloClient.mutate({
      mutation: CART_LINES_UPDATE,
      variables: {
        cartId,
        lines,
      },
      context: {
        retryOnFailure: true,
      },
      errorPolicy: 'all',
    })

    const sharedErrorContext: ErrorContext = {
      scope: 'cart',
      error,
      userErrors: data?.cartLinesUpdate.userErrors,
    }

    // report any userErrors or GraphQL errors that are present
    this.reportErrors('updateLines', sharedErrorContext)

    if (!data) {
      reportClientError({
        error: new Error('updateLine :: no data returned from CART_LINES_UPDATE mutation'),
        context: {
          ...sharedErrorContext,
          label: 'updateLines missing data',
        },
      })
      throw new Error('cart lines update failed: missing data.')
    }

    if (!data.cartLinesUpdate.cart?.id) {
      reportClientError({
        error: new Error('updateLines :: no cart ID in response'),
        context: {
          ...sharedErrorContext,
          label: 'updateLines missing cart id',
        },
      })
      throw new Error('cart lines update failed: missing cart ID.')
    }

    return data.cartLinesUpdate.cart
  }

  /**
   * Set cart-level attributes for the given cart. Setting attributes replaces any existing attributes.
   * @param props.cartId the id of the cart to update the attributes of
   * @param props.attributes the attributes to set on the cart
   * @returns the updated cart if successful, `null` otherwise
   */
  public async setCartAttributes({
    cartId,
    attributes,
  }: Parameters<ICheckoutClient['setCartAttributes']>[0]): ReturnType<
    ICheckoutClient['setCartAttributes']
  > {
    let { data, error } = await this.apolloClient.mutate({
      mutation: CART_ATTRIBUTES_UPDATE,
      variables: {
        cartId,
        attributes,
      },
      context: {
        retryOnFailure: true,
      },
      errorPolicy: 'all',
    })

    const sharedErrorContext = {
      scope: 'cart',
      error,
      userErrors: data?.cartAttributesUpdate.userErrors,
    }

    // report any userErrors or GraphQL errors that are present
    this.reportErrors('setCartAttributes', sharedErrorContext)

    if (!data) {
      reportClientError({
        error: new Error(
          'setCartAttributes :: no data returned from CART_ATTRIBUTES_UPDATE mutation'
        ),
        context: {
          ...sharedErrorContext,
          label: 'setCartAttributes missing data',
        },
      })
      throw new Error('set cart attributes failed: missing data.')
    }

    if (!data.cartAttributesUpdate.cart?.id) {
      reportClientError({
        error: new Error('setCartAttributes :: no cart ID in response'),
        context: {
          ...sharedErrorContext,
          label: 'setCartAttributes missing cart ID',
        },
      })
      throw new Error('set cart attributes failed: missing cart ID.')
    }

    return data.cartAttributesUpdate.cart
  }

  /**
   * Retrieves the `checkoutUrl` for a given cart by id
   * @param cartId the id of the cart to retrieve a `checkoutUrl` for
   * @param cartAttributes the current attributes set on the cart
   * @param queryParams query params to append to the `checkoutUrl` for discounts, 3rd party tracking, etc.
   * @throws ApolloError
   * @returns `checkoutUrl` as string if present, else null
   */
  public async fetchCheckoutUrl({
    cartId,
    cartAttributes,
    queryParams,
  }: Parameters<ICheckoutClient['fetchCheckoutUrl']>[0]): ReturnType<
    ICheckoutClient['fetchCheckoutUrl']
  > {
    // prior to fetching the checkout url, we must merge in any updated values from `this._customAttributes`.
    // this ensures those values are up to date prior to checkout.
    this.setCartAttributes({ cartId, attributes: [...cartAttributes, ...this._customAttributes] })

    let result: string | null = null
    const { data, error } = await this.apolloClient.query({
      query: FETCH_CHECKOUT_URL,
      variables: {
        cartId,
      },
      fetchPolicy: 'network-only',
    })

    const sharedErrorContext: ErrorContext = {
      scope: 'checkout',
      error,
      userErrors: data?.retrieveCheckoutUrl.userErrors,
    }

    // report any userErrors or GraphQL errors that are present
    this.reportErrors('fetchCheckoutUrl', sharedErrorContext)

    if (data?.retrieveCheckoutUrl.checkoutUrl) {
      let checkoutUrl
      try {
        checkoutUrl = new URL(data.retrieveCheckoutUrl.checkoutUrl)
      } catch {
        checkoutUrl = null
      }

      if (checkoutUrl) {
        const discountCode = globalThis.sessionStorage.getItem(sessionStorageKeys.discount)

        if (discountCode) {
          checkoutUrl.searchParams.append('discount', discountCode)
        }

        if (queryParams) {
          Object.entries(queryParams).forEach(([key, value]) => {
            // if we already retrieved a discount from sessionStorage, skip others
            if (!(key === 'discount' && discountCode)) {
              checkoutUrl.searchParams.append(key, value)
            }
          })
        }
      }

      if (checkoutUrl) result = checkoutUrl.toString()
    } else {
      reportClientError({
        error: new Error('fetchCheckoutUrl :: no data returned from FETCH_CHECKOUT_URL query'),
        context: {
          ...sharedErrorContext,
          label: 'fetchCheckoutUrl missing data',
        },
      })
      throw new Error('checkout url retrieve failed: missing data.')
    }

    return result
  }
}
